/**
 * PATH: packages/email-hub/src/feature/components/SignatureEditorModal.tsx
 * FILE_ID: EMAILHUB-FEATURE-COMPONENT-SIGEDITORMODAL-001
 * PURPOSE: Proper rich-text editor modal for the operator's email
 *          signature — replaces the window.prompt() the Signature
 *          button used to open.
 * WHY:     A native prompt() is a single-line box that expects raw
 *          HTML — pasting a real multi-line signature was impossible
 *          ("can't put signature properly here", 2026-06-10). This
 *          modal gives a WYSIWYG editor with live formatting, plus an
 *          explicit "Save & insert now" so the signature lands in the
 *          CURRENT draft, not just the next one.
 * FLOW:
 *   open → load saved signature (localStorage cache) → edit in
 *   RichTextEditor → Save (localStorage + durable per-user store) /
 *   Save & insert now (also into the open draft) / Clear / Cancel
 * RELATED:
 *   - pages/useComposePage.ts: owns open state + the insert-now hook.
 *   - pages/_composeHelpers.ts: SIGNATURE_STORAGE_KEY +
 *     persistSignatureToDb (cross-device durability).
 */
import { useEffect, useRef, useState } from 'react'
import { PenLine, X } from 'lucide-react'
import RichTextEditor, { type RichTextEditorHandle } from './RichTextEditor'
import { wrapSignatureLayout, expandSignatureBlocks } from './rich-text/signatureBlock'
import {
  SIGNATURE_STORAGE_KEY,
  persistSignatureToDb,
  loadSignatureForScope,
  saveSignatureForAccount,
  scopeIsOptedOut,
  NO_SIGNATURE_SENTINEL,
} from '../pages/_composeHelpers'

/** A mailbox the operator can hold a distinct signature for. */
export interface SignatureAccount {
  id: number
  email_address: string
  display_name?: string
}

function readSavedSignature(): string {
  try {
    return window.localStorage.getItem(SIGNATURE_STORAGE_KEY) || ''
  } catch {
    return ''
  }
}

/** Persist (or clear, when html is blank) the signature to both the
 *  instant localStorage cache and the durable per-user store.
 *  I→O: html → 'saved' | 'cleared' | 'error'. */
export function writeSignature(html: string): 'saved' | 'cleared' | 'error' {
  const trimmed = (html || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, '').trim()
  try {
    if (trimmed) {
      window.localStorage.setItem(SIGNATURE_STORAGE_KEY, html)
      persistSignatureToDb(html)
      return 'saved'
    }
    window.localStorage.removeItem(SIGNATURE_STORAGE_KEY)
    persistSignatureToDb('')
    return 'cleared'
  } catch {
    return 'error'
  }
}

/** Persist a signature for a chosen scope: the default mailbox-agnostic
 *  signature when ``scopeEmail`` is '' (existing behaviour), otherwise a
 *  per-mailbox override. I→O: → 'saved' | 'cleared' | 'error'. */
export function writeSignatureForScope(
  scopeEmail: string, html: string,
): 'saved' | 'cleared' | 'error' {
  if (!scopeEmail) return writeSignature(html)
  const trimmed = (html || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, '').trim()
  try {
    saveSignatureForAccount(scopeEmail, html)
    return trimmed ? 'saved' : 'cleared'
  } catch {
    return 'error'
  }
}

export interface SignatureEditorModalProps {
  open: boolean
  onClose: () => void
  /** Called after a successful save with the saved html ('' = cleared). */
  onSaved?: (html: string, outcome: 'saved' | 'cleared') => void
  /** When provided, renders "Save & insert now" which also drops the
   * signature into the current draft at the caret. */
  onInsertNow?: (html: string) => void
  /** Surface storage failures to the operator (toast). */
  onError?: (message: string) => void
  /** Connected mailboxes — when 2+, a scope picker lets the operator
   *  keep a distinct signature per mailbox (falling back to the default
   *  for any mailbox without its own). */
  accounts?: SignatureAccount[]
}

export default function SignatureEditorModal({
  open, onClose, onSaved, onInsertNow, onError, accounts,
}: SignatureEditorModalProps) {
  const [html, setHtml] = useState<string>('')
  // '' = the default (all-mailboxes) signature; otherwise a mailbox addr.
  const [scope, setScope] = useState<string>('')
  // When true the operator has deliberately chosen NO signature for this
  // scope — suppresses even the Quotech-branded default on send.
  const [optedOut, setOptedOut] = useState<boolean>(false)
  const editorRef = useRef<RichTextEditorHandle | null>(null)

  const sendable = (accounts || []).filter((a) => typeof a.id === 'number' && a.email_address)
  const showScopePicker = sendable.length > 1

  // (Re)load the signature for the current scope — on open AND whenever
  // the operator switches mailbox — so abandoned edits don't linger and
  // each scope shows its own saved value.
  useEffect(() => {
    if (!open) return
    const saved = scope ? loadSignatureForScope(scope) : readSavedSignature()
    const isOptedOut = scopeIsOptedOut(scope)
    setOptedOut(isOptedOut)
    // A real layout signature (Outlook/Apple-Mail table) is seeded as an
    // OPAQUE block so the editor renders a faithful preview instead of
    // re-shattering its table on parse. Plain signatures pass through as-is.
    // Opt-out seeds a blank editor (sentinel is hidden by loadSignatureForScope).
    const seed = isOptedOut ? '' : wrapSignatureLayout(saved)
    setHtml(seed)
    requestAnimationFrame(() => editorRef.current?.setHtml(seed))
  }, [open, scope])

  if (!open) return null

  const commit = (alsoInsert: boolean) => {
    // Opt-out persists the sentinel so the resolver suppresses any signature.
    if (optedOut) {
      const outcome = writeSignatureForScope(scope, NO_SIGNATURE_SENTINEL)
      if (outcome === 'error') {
        onError?.('Could not save signature (browser storage unavailable).')
        return
      }
      onSaved?.('', 'cleared')
      onClose()
      return
    }
    // STORE the raw, reusable signature HTML: expand any opaque block back to
    // its real markup so the stored value is a normal signature (re-loadable,
    // sendable) and the blank-check sees the real text inside it.
    const stored = expandSignatureBlocks(html)
    const outcome = writeSignatureForScope(scope, stored)
    if (outcome === 'error') {
      onError?.('Could not save signature (browser storage unavailable).')
      return
    }
    onSaved?.(outcome === 'saved' ? stored : '', outcome)
    // "Insert now" drops it into the live compose editor — hand over the
    // OPAQUE-block form so a layout signature lands faithfully there too.
    if (alsoInsert && outcome === 'saved') onInsertNow?.(wrapSignatureLayout(stored))
    onClose()
  }

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Edit email signature"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div className="w-full max-w-2xl bg-background border border-border rounded-xl shadow-xl flex flex-col max-h-[85vh]">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h2 className="text-base font-semibold text-foreground flex items-center gap-2">
            <PenLine className="h-4 w-4 text-muted-foreground" />
            Email signature
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-accent"
            aria-label="Close signature editor"
          >
            <X className="h-4 w-4 text-muted-foreground" />
          </button>
        </div>

        <div className="p-4 flex-1 overflow-y-auto">
          <p className="text-xs text-muted-foreground mb-3">
            Used on every new message, reply and forward. Saved to your
            account, so it follows you across devices. Paste a formatted
            signature from Outlook or Apple Mail and it keeps its exact
            layout, logo and colours.
          </p>
          {showScopePicker && (
            <label className="flex flex-col gap-1 mb-3 text-xs text-muted-foreground">
              Signature for
              <select
                value={scope}
                onChange={(e) => setScope(e.target.value)}
                className="px-2 py-1.5 border border-border rounded bg-background text-foreground text-sm focus:outline-none focus:border-primary"
              >
                <option value="">All mailboxes (default)</option>
                {sendable.map((a) => (
                  <option key={a.id} value={a.email_address}>
                    {a.display_name ? `${a.display_name} <${a.email_address}>` : a.email_address}
                  </option>
                ))}
              </select>
              <span className="text-[11px] text-muted-foreground/80">
                A mailbox without its own signature uses the default.
              </span>
            </label>
          )}
          <label className="flex items-start gap-2 mb-3 text-xs text-foreground cursor-pointer select-none">
            <input
              type="checkbox"
              checked={optedOut}
              onChange={(e) => setOptedOut(e.target.checked)}
              className="mt-0.5 h-3.5 w-3.5 accent-primary"
            />
            <span>
              Don&apos;t add any signature
              <span className="block text-[11px] text-muted-foreground">
                {scope
                  ? 'This mailbox sends with no signature — not even the Quotech default.'
                  : 'New messages, replies and forwards send with no signature — not even the Quotech default.'}
              </span>
            </span>
          </label>
          <div className={optedOut ? 'opacity-40 pointer-events-none' : ''} aria-hidden={optedOut}>
            <RichTextEditor
              ref={editorRef}
              value={html}
              onChange={setHtml}
              placeholder={'e.g.\nJake Smith\nAll Styled Steel - 0400 000 000'}
              ariaLabel="Signature body"
              className="min-h-[180px]"
              signatureMode
            />
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 p-4 border-t border-border">
          <button
            type="button"
            onClick={() => {
              setHtml('')
              setOptedOut(false)
              editorRef.current?.setHtml('')
            }}
            className="text-xs px-3 py-2 rounded text-destructive hover:bg-destructive/10"
          >
            Clear signature
          </button>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="text-sm px-3 py-2 rounded border border-border hover:bg-accent text-foreground"
            >
              Cancel
            </button>
            {onInsertNow && !optedOut && (
              <button
                type="button"
                onClick={() => commit(true)}
                className="text-sm px-3 py-2 rounded border border-primary/40 text-primary hover:bg-primary/10"
              >
                Save &amp; insert now
              </button>
            )}
            <button
              type="button"
              onClick={() => commit(false)}
              className="text-sm px-4 py-2 rounded bg-primary text-primary-foreground hover:bg-primary/90"
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
